import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { connect } from "react-redux";
import poiActions from "../../pois/actions";
import ListItem from "../../pois/components/listItem";
import ListItemHotel from "../../pois/components/listItemHotel";
import CollectionListItem from "../../pois/components/collectionListItem";
import { createDetailUrlFromResource } from "../../../../server/utils/createUrls";
import resourceTypes from "../../../../server/lib/open-planet/resourceTypes";
import { getListImage } from "../../shared/getListImage";
import Ad from "../../shared/components/ad";
import { getAdData } from "../../shared/utils/createAdTargeting";
import { getPartnerActivityPrices } from "../../shared/utils/getPartnerActivityPrices";
import orderedHotelPerks from "../constants/hotelPerks";
import ListItemWireframe from "./listItemWireframe";

/**
 * The actual list of items component
 * @augments React.Component<ListItemProps, {}>
 */
export default class ListItems extends React.Component {
  static propTypes = {
    pois: PropTypes.array,
    type: PropTypes.string,
    params: PropTypes.object,
    adWrapperStyle: PropTypes.object,
    breadcrumbs: PropTypes.array,
    mapMode: PropTypes.string,
    fetchingNext: PropTypes.bool,
    mobile: PropTypes.bool,
    onShowPrices: PropTypes.func,
    dispatch: PropTypes.func,
    availability: PropTypes.object,
    lpCurrency: PropTypes.string,
    poiDetailsHost: PropTypes.string,
  };

  static _getPerks({ perks }) {
    return perks && perks
      .sort((a, b) => orderedHotelPerks.indexOf(a) - orderedHotelPerks.indexOf(b))
      .slice(0, 4);
  }

  static _getImages = getListImage;

  static _getPlace(poi) {
    return poi.containingPlace
      ? {
          name: poi.containingPlace.name,
          type: poi.containingPlace.placeType,
          center: poi.containingPlace.center,
        }
      : {};
  }

  static _getArticleProps(poi) {
    if (poi.type !== resourceTypes.articles) {
      return {};
    }

    const description = poi.content[0] && poi.content[0].text;
    const imageMatch = description.match(/src="([^"]+)"/);
    let image = {};

    if (imageMatch[1]) {
      const base = "https://lonelyplanetwp.imgix.net/";
      const replaced = imageMatch[1].replace(
        "http://www.lonelyplanet.com/travel-blog/tip-article/wordpress_uploads/",
        base,
      );
      const query = "?fit=min&q=40&sharp=10&vib=20&w=1470";

      image = {
        path: `${replaced}${query}`,
        orientation: "portrait",
      };
    }

    return {
      title: poi.title,
      description,
      image,
    };
  }

  static _isTopChoice(poi) {
    const attributesArray = poi.poiAttributes || poi.attributes || [];
    const topChoice = attributesArray.filter(
      attribute => attribute.name === "top_choice",
    );
    return topChoice.length > 0;
  }

  static _getCollectionItemProps(poi) {
    if (poi.narrativePlace && poi.type === "place") {
      return {
        title: poi.narrativePlace.name,
        mentions: poi.mentions,
      };
    }
    return { mentions: poi.mentions };
  }

  static _getPlaceProps(poi) {
    if (poi.type === "place") {
      let subtype;
      if (poi.ancestry && Array.isArray(poi.ancestry)) {
        subtype = poi.ancestry[0].name;
      } else if (poi.ancestry) {
        subtype = poi.ancestry.name;
      } else {
        subtype = "Place";
      }

      return {
        description: poi.review ? poi.review.essential : "",
        slug: `https://www.lonelyplanet.com/places/${poi.id}`,
        subtype,
      };
    }
    return {};
  }

  constructor(props) {
    super(props);

    this.state = {};
    this.ads = {};

    ["onMouseEnter", "onMouseLeave"].forEach(fn => {
      this[fn] = this[fn].bind(this);
    });
  }

  onMouseEnter(poi) {
    if (!poi.location) return;

    clearTimeout(this.leaveTimer);
    this.enterTimer = setTimeout(() => {
      this.props.dispatch(poiActions.hoverPlace(poi));
    }, 200);
  }

  onMouseLeave() {
    clearTimeout(this.enterTimer);
    this.leaveTimer = setTimeout(
      () => this.props.dispatch(poiActions.hoverPlace({})),
      200,
    );
  }

  _getPartnerActivityInfo(poi, kind, lpCurrency) {
    if (poi.type !== resourceTypes.partner_activity) {
      return {};
    }

    const { discountedPrice, regularPrice } = getPartnerActivityPrices(lpCurrency, poi.prices);
    return {
      duration: poi.duration,
      durationInfo: poi.durationInfo,
      difficulty: poi.difficulty.description,
      groupSize: {
        min: poi.groupSize.minimum_size || 1,
        max: poi.groupSize.maximum_size,
      },
      provider: poi.provider,
      tourStart: poi.departureInfo,
      tourEnd: poi.returnInfo,
      tourMap: poi.links.map,
      image: (poi.images || [])[0],
      bookingUrl: poi.links.booking,
      subtype: poi.canonicalCategory,
      description: poi.descriptions,
      price: {
        amount: discountedPrice || regularPrice,
        currency: lpCurrency,
      },
      activityType: kind,
      mobile: this.props.mobile,
    };
  }

  _getSlug(poi) {
    const { breadcrumbs, params, poiDetailsHost } = this.props;

    return poiDetailsHost + createDetailUrlFromResource(
      poi,
      breadcrumbs.map(b => b.title),
      params.placeId,
    );
  }

  _getLodgingProps(poi) {
    const { availability } = this.props;
    const hasPrices = poi.offerings && poi.offerings.length;
    const price = availability && hasPrices
      ? {
          price: {
            amount: parseInt(poi.offerings[0].cost.amount, 10),
            currency: availability.currency,
            rate:
              availability.numNights > 0
                ? `${availability.numNights} night${
                    availability.numNights !== 1 ? "s" : ""
                  }`
                : "",
          },
        }
      : {};

    return Object.assign({},
      this._getListItemProps(poi.lodging),
      price,
      {
        image: (poi.images || [])[0],
        lodgingId: `poi-${poi.lodging.id}`,
        soldOut: availability && !hasPrices,
      },
    );
  }

  _getListItemProps(poi) {
    return {
      id: `poi-${poi.id}`,
      title: poi.name,
      slug: this._getSlug(poi),
      type: poi.resource || poi.type,
      subtype: poi.subtypes ? poi.subtypes[0] : "",
      place: ListItems._getPlace(poi),
      image: (poi.images || [])[0],
      description: poi.review ? poi.review.essential : "",
      address: poi.address,
      perks: ListItems._getPerks(poi),
      topChoice: ListItems._isTopChoice(poi),
      reviewed: poi.review && true,
      location: poi.location,
      providers: poi.providers || null,
      hidePrice:
        poi.type === resourceTypes.lodging &&
        (!this.props.availability.startDate ||
          !this.props.availability.endDate),
      onShowPrices: () => {
        if (this.props.onShowPrices) {
          this.props.onShowPrices();
        }
      },
      mobile: this.props.mobile,
    };
  }

  render() {
    const {
      pois,
      type,
      adWrapperStyle,
      mapMode,
      fetchingNext,
      mobile,
      lpCurrency,
    } = this.props;

    let adsData = {};
    if (typeof window !== "undefined") {
      adsData = {
        ...getAdData(window.lp.ads, window.location.url, window.Krux),
        position: "superzone",
      };
    }

    const items = pois.map((poi, i) => {
      const isLodgingType = type.resource === resourceTypes.lodging;
      const hasAvailability = poi.type === resourceTypes.availability;
      let ListItemType;

      if (type.resource === resourceTypes.collections && poi.newExperience) {
        ListItemType = CollectionListItem;
      } else if (isLodgingType) {
        ListItemType = ListItemHotel;
      } else {
        ListItemType = type.component || ListItem;
      }

      const partnerActivityProps = this._getPartnerActivityInfo(poi, type.kind, lpCurrency);
      const collectionProps = ListItems._getCollectionItemProps(poi);
      const listItemProps = hasAvailability
          ? this._getLodgingProps(poi)
          : this._getListItemProps(poi);

      const articleProps = ListItems._getArticleProps(poi);
      const placeProps = ListItems._getPlaceProps(poi);

      const showAd = i % 10 === 2;

      return (
        <div
          className="ListItem-container"
          key={poi.name}
          onMouseEnter={() => {
            if (mapMode === "hover") {
              this.onMouseEnter(hasAvailability && isLodgingType ? poi.lodging : poi);
            }
          }}
          onMouseLeave={() => {
            if (mapMode === "hover") {
              this.onMouseLeave();
            }
          }}
        >
          <ListItemType
            {...listItemProps}
            {...partnerActivityProps}
            {...articleProps}
            {...collectionProps}
            {...placeProps}
          />

          {showAd && (
            <div
              className="Ad"
              style={adWrapperStyle}
              ref={node => {
                this.ads[`ad-${poi.id}`] = node;
              }}
            >
              <Ad
                adUnitPath="/9885583/LonelyPlanet.com/Destinations/Lists/superzone"
                id={`lists-superzone-inline-${i}`}
                sizeMapping={[
                  { viewport: [768, 0], slot: [[1, 1], [300, 250], [320, 50]] },
                  { viewport: [1024, 0], slot: [[1, 1], [728, 90]] },
                ]}
                targeting={adsData}
              />
            </div>
          )}
        </div>
      );
    });

    return (
      <div
        className={cn(
          "ListItems-container",
          type.resource === "lodging" && "ListItems-container-hotels",
        )}
      >
        {items}
        {fetchingNext && (
          <ListItemWireframe type={mobile ? "mobile" : "desktop"} />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  availability: state.availability,
});

const connected = connect(mapStateToProps)(ListItems);
export { connected };



// WEBPACK FOOTER //
// ./assets/list/components/listItems.jsx